import React from 'react'
import { useInView } from '../useInView'
import {
  SiTypescript,
  SiJavascript,
  SiReact,
  SiNextdotjs,
  SiPython,
  SiKotlin,
  SiTensorflow,
  SiFastapi,
  SiOpenai,
  SiMicrosoftazure,
  SiDocker,
  SiTerraform,
  SiLinux,
  SiGit,
  SiGithubactions,
  SiPrometheus,
  SiGrafana,
} from 'react-icons/si'
import { FaJava } from 'react-icons/fa'
import { HiDatabase } from 'react-icons/hi'
import { FiArrowUpRight, FiTerminal } from 'react-icons/fi'

const skillGroups = [
  {
    id: 'development',
    number: '01',
    title: 'Software development',
    description: 'Applications, API integrations and team projects — from Anbud to PricePulse.',
    skills: [
      { name: 'TypeScript', icon: SiTypescript, color: '#3178c6' },
      { name: 'JavaScript', icon: SiJavascript, color: '#f7df1e' },
      { name: 'React', icon: SiReact, color: '#61dafb' },
      { name: 'Next.js', icon: SiNextdotjs, color: '#f0ebe3' },
      { name: 'Python', icon: SiPython, color: '#5a9fd4' },
      { name: 'Java', icon: FaJava, color: '#ed8b00' },
      { name: 'Kotlin', icon: SiKotlin, color: '#b58aff' },
      { name: 'SQL', icon: HiDatabase, color: '#c9fb35' },
    ],
    evidence: 'PricePulse / Android team project',
    href: 'https://github.com/SakariaAhmed/PricePulse',
  },
  {
    id: 'ai',
    number: '02',
    title: 'Applied AI',
    description: 'Model training and inference with MNIST. Document search and RAG in Anbud.',
    skills: [
      { name: 'TensorFlow / Keras', icon: SiTensorflow, color: '#ff8b34' },
      { name: 'FastAPI', icon: SiFastapi, color: '#43c7b5' },
      { name: 'OpenAI API', icon: SiOpenai, color: '#f0ebe3' },
      { name: 'RAG / pgvector', icon: HiDatabase, color: '#c9fb35' },
    ],
    evidence: 'MNIST / training to a running service',
    href: 'https://github.com/SakariaAhmed/Neural-Network-MNIST',
  },
  {
    id: 'infrastructure',
    number: '03',
    title: 'Cloud & operations',
    description: 'Azure deployment in Anbud. Infrastructure, monitoring and recovery in my Linux homelab.',
    skills: [
      { name: 'Azure', icon: SiMicrosoftazure, color: '#42a5f5' },
      { name: 'Terraform', icon: SiTerraform, color: '#b09aff' },
      { name: 'Docker', icon: SiDocker, color: '#2496ed' },
      { name: 'Linux / SSH', icon: SiLinux, color: '#fcc624' },
      { name: 'Git', icon: SiGit, color: '#f05032' },
      { name: 'GitHub Actions', icon: SiGithubactions, color: '#63a6f5' },
      { name: 'Prometheus', icon: SiPrometheus, color: '#e76c50' },
      { name: 'Grafana', icon: SiGrafana, color: '#f6a343' },
    ],
    evidence: 'Linux homelab / deployment & recovery',
    href: 'https://github.com/SakariaAhmed/Linux-Homelab',
  },
]

const Skills = () => {
  const [ref, isInView] = useInView()
  const marqueeText = 'TypeScript · React · Python · AI · Azure · Terraform · Docker · Linux'

  return (
    <section id="skills" aria-labelledby="skills-title" className="py-20 md:py-32 relative">
      <div aria-hidden="true" className="mb-14 md:mb-20 overflow-hidden border-y border-edge py-5 sm:py-6">
        <div className="inline-flex animate-marquee motion-reduce:animate-none">
          {[0, 1].map(copy => (
            <span key={copy} className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-display font-extrabold text-cream/[0.04] whitespace-nowrap pr-8">
              {marqueeText}&nbsp;&nbsp;&middot;&nbsp;&nbsp;
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 sm:px-8" ref={ref}>
        <div className={`flex items-center gap-4 mb-10 sm:mb-12 transition-all duration-700 motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:transform-none ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <span className="font-mono text-lime text-sm tracking-wider">02</span>
          <h2 id="skills-title" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold text-cream">
            Skills
          </h2>
          <div className="h-px bg-edge flex-grow" />
        </div>

        <p className="text-cream-dim text-base sm:text-lg mb-8 sm:mb-10 max-w-2xl leading-relaxed font-light">
          From application code and AI services to deployment, monitoring and recovery.
          A toolkit built through my studies and hands-on projects.
        </p>

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-5">
          {skillGroups.map(group => (
            <article key={group.id} aria-labelledby={`skills-${group.id}`} className="flex flex-col min-w-0 p-5 sm:p-6 bg-dark-surface rounded-2xl border border-edge">
              <span className="font-mono text-lime text-xs mb-4" aria-hidden="true">{group.number} /</span>
              <h3 id={`skills-${group.id}`} className="font-display font-bold text-xl text-cream tracking-tight">
                {group.title}
              </h3>
              <p className="text-sm text-cream-dim leading-relaxed mt-3 mb-6 lg:min-h-[5.25rem]">
                {group.description}
              </p>
              <ul className="grid grid-cols-2 lg:grid-cols-1 gap-2 sm:gap-3">
                {group.skills.map(skill => {
                  const Icon = skill.icon
                  return (
                    <li key={skill.name} className="flex items-center gap-2.5 rounded-xl bg-dark-elevated/60 p-3 min-w-0">
                      <Icon aria-hidden="true" className="text-xl shrink-0" style={{ color: skill.color }} />
                      <span className="text-xs sm:text-sm text-cream leading-snug">{skill.name}</span>
                    </li>
                  )
                })}
              </ul>
              {group.id === 'ai' && (
                <div className="border-t border-edge mt-5 pt-5">
                  <p className="text-cream text-sm font-medium flex items-center gap-2">
                    <FiTerminal aria-hidden="true" className="text-lime" /> Agentic coding
                  </p>
                  <p className="text-sm text-cream mt-2">Codex · Claude Code</p>
                  <p className="text-xs text-cream-dim leading-relaxed mt-2">
                    Implementation, debugging and code review, with testing and verification of the output.
                  </p>
                </div>
              )}
              <a href={group.href} target="_blank" rel="noopener noreferrer" className="mt-auto pt-6 text-xs text-cream-dim hover:text-lime focus-visible:outline focus-visible:outline-2 focus-visible:outline-lime focus-visible:outline-offset-4 rounded-sm leading-relaxed inline-flex items-center gap-2">
                <span>{group.evidence}</span><FiArrowUpRight aria-hidden="true" className="shrink-0" />
              </a>
            </article>
          ))}
        </div>

        <div className="mt-5 sm:mt-6 border border-lime/20 bg-lime/[0.03] rounded-2xl px-5 py-5 sm:px-6 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6">
          <span className="font-mono text-lime text-xs uppercase tracking-[0.12em] shrink-0 sm:pt-1">Currently learning</span>
          <p className="text-sm text-cream-dim leading-relaxed">
            <span className="text-cream">Azure administration / AZ-104 exam preparation</span>
            <span className="block mt-1">Building on hands-on Azure and Terraform practice.</span>
          </p>
        </div>
      </div>
    </section>
  )
}

export default Skills
